import { useMemo, useRef, useState } from "react";
import { Api, ApiError, type OcrEstimate, type Source } from "../api.js";
import { LIMITS } from "../draft.js";
import { PAGE_SIZE, classifyFile, countDocs, filterDocs, pageOf, runPool, MAX_FILE_MB, type DocFilter } from "../documents.js";
import { useI18n } from "../i18n.js";
import { Badge, Field, Section } from "./ui.js";

const TONE = { pending: "info", indexing: "info", indexed: "ok", error: "bad", unsupported: "muted" } as const;
const UPLOADS_AT_ONCE = 3;
const REMOVALS_AT_ONCE = 5;

interface Props {
  api: Api;
  agentId: string;
  sources: Source[];
  /** Changes the list shown here without asking the server (the server list is read again when a batch is over). */
  setSources: (fn: (l: Source[]) => Source[]) => void;
  /** Reads the list again and keeps watching documents that are still being trained. */
  refresh: () => Promise<void>;
  setNotice: (m: string) => void;
}

/** A PDF whose pages (all or some) have no text: the ones that can be read with OCR. */
const needsOcr = (s: Source) => /\.pdf$/i.test(s.name) && ((s.status === "error" && /no selectable text/.test(s.error ?? "")) || (s.status === "indexed" && /pages? had no text/.test(s.error ?? "")));
const reading = (s: Source) => s.status === "indexing" && /^Reading scanned pages/.test(s.error ?? "");

/** "Read scanned pages": shows what it would read and cost first; nothing starts until the owner confirms that price. */
function OcrControl({ api, agentId, source, refresh }: { api: Api; agentId: string; source: Source; refresh: () => Promise<void> }) {
  const { t } = useI18n();
  const [est, setEst] = useState<OcrEstimate | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const look = async () => {
    setBusy(true);
    setMsg(null);
    try {
      setEst(await api.ocrEstimate(agentId, source.id));
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : t("ocr.failed"));
    } finally {
      setBusy(false);
    }
  };
  const go = async () => {
    if (!est) return;
    setBusy(true);
    try {
      await api.ocrStart(agentId, source.id, est.usd);
      setEst(null);
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === "estimate_changed") {
        await look();
        setMsg(t("ocr.changed"));
      } else setMsg(e instanceof ApiError ? e.message : t("ocr.failed"));
    } finally {
      setBusy(false);
    }
  };
  const stop = async () => {
    setBusy(true);
    try {
      await api.ocrStop(agentId, source.id);
    } catch {
      /* the next refresh shows what happened */
    } finally {
      setBusy(false);
    }
  };
  if (reading(source))
    return (
      <button type="button" className="btn ghost" disabled={busy} onClick={() => void stop()}>
        {t("ocr.stop")}
      </button>
    );
  if (!needsOcr(source)) return null;
  return (
    <span className="ocr">
      {!est && (
        <button type="button" className="btn ghost" disabled={busy} onClick={() => void look()} aria-label={t("ocr.buttonFor", { name: source.name })}>
          {busy ? t("ocr.checking") : t("ocr.button")}
        </button>
      )}
      {est && (
        <span className="row" role="group" aria-label={t("ocr.group", { name: source.name })}>
          {est.allowed ? (
            <>
              <span>{t("ocr.summary", { n: est.scannedPages, usd: est.usd.toFixed(2), min: est.minutes })}</span>
              <button type="button" className="btn secondary" disabled={busy} onClick={() => void go()}>
                {t("ocr.start")}
              </button>
            </>
          ) : (
            <span role="alert">{est.reasons.map((r) => t(`ocr.r.${r}` as never)).join(" ")}</span>
          )}
          <button type="button" className="btn ghost" onClick={() => setEst(null)}>
            {t("ocr.cancel")}
          </button>
        </span>
      )}
      {msg && <span role="alert">{msg}</span>}
    </span>
  );
}

type Job = { kind: "upload" | "remove" | "retry"; done: number; total: number } | null;

/**
 * Training documents, made for hundreds of files: uploads and removals run a few at a time with a count, the list is searched,
 * filtered and shown 25 at a time, rows can be selected (a page, or everything that matches) and removed together,
 * and everything that needs attention can be retried in one go.
 */
export function DocumentsPanel({ api, agentId, sources, setSources, refresh, setNotice }: Props) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<DocFilter>("all");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const [job, setJob] = useState<Job>(null);
  const stop = useRef(false);

  const docs = useMemo(() => sources.filter((s) => s.type === "file"), [sources]);
  const counts = useMemo(() => countDocs(docs), [docs]);
  const matching = useMemo(() => filterDocs(docs, q, filter), [docs, q, filter]);
  const view = pageOf(matching, page);
  const onPage = view.items.map((d) => d.id);
  const allOnPage = onPage.length > 0 && onPage.every((id) => selected.has(id));
  const busy = job !== null;

  /** The server receives only a few big files at once; when it says it is busy, wait a moment and try that file again. */
  const uploadOne = async (file: File): Promise<Source> => {
    for (let attempt = 0; ; attempt++) {
      try {
        return await api.uploadSourceFile(agentId, file);
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 503 && e.code === "busy") || attempt >= 5 || stop.current) throw e;
        await new Promise((r) => setTimeout(r, 4000));
      }
    }
  };

  const upload = async (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (!list.length) return;
    const ok: File[] = [];
    const skipped: string[] = [];
    for (const file of list) {
      const v = classifyFile(file);
      if (v === "too_big") skipped.push(t("docs.tooBig", { name: file.name, mb: MAX_FILE_MB }));
      else if (v === "unsupported_type") skipped.push(t("docs.badType", { name: file.name }));
      else ok.push(file);
    }
    stop.current = false;
    let limitHit = false;
    let failed = 0;
    if (ok.length) {
      setJob({ kind: "upload", done: 0, total: ok.length });
      const res = await runPool(
        ok,
        UPLOADS_AT_ONCE,
        async (file) => {
          try {
            const s = await uploadOne(file);
            setSources((l) => [...l.filter((x) => x.id !== s.id), s]);
          } catch (e) {
            if (e instanceof ApiError && e.status === 409) {
              limitHit = true;
              stop.current = true;
            } else {
              failed++;
              skipped.push(e instanceof ApiError ? `${file.name}: ${e.message}` : t("docs.failedOne", { name: file.name }));
            }
          }
        },
        { shouldStop: () => stop.current, onProgress: (done) => setJob({ kind: "upload", done, total: ok.length }) },
      );
      void res;
    }
    setJob(null);
    if (limitHit) setNotice(t("docs.limit", { max: LIMITS.documentsMax }));
    else if (skipped.length) setNotice(t("docs.skipped", { n: skipped.length, first: skipped[0] ?? "" }));
    else if (failed) setNotice(t("docs.someFailed", { n: failed }));
    await refresh();
  };

  const removeMany = async (ids: string[]) => {
    stop.current = false;
    setConfirming(false);
    setJob({ kind: "remove", done: 0, total: ids.length });
    const gone = new Set<string>();
    const res = await runPool(
      ids,
      REMOVALS_AT_ONCE,
      async (id) => {
        await api.removeSource(agentId, id);
        gone.add(id);
      },
      { shouldStop: () => stop.current, onProgress: (done) => setJob({ kind: "remove", done, total: ids.length }) },
    );
    setSources((l) => l.filter((s) => !gone.has(s.id)));
    setSelected(new Set());
    setJob(null);
    if (res.failed.length) setNotice(t("docs.someFailed", { n: res.failed.length }));
  };

  const retryMany = async (ids: string[]) => {
    stop.current = false;
    setJob({ kind: "retry", done: 0, total: ids.length });
    const res = await runPool(ids, UPLOADS_AT_ONCE, async (id) => void (await api.reindexSource(agentId, id)), { shouldStop: () => stop.current, onProgress: (done) => setJob({ kind: "retry", done, total: ids.length }) });
    setJob(null);
    if (res.failed.length) setNotice(t("docs.someFailed", { n: res.failed.length }));
    await refresh();
  };

  const toggle = (id: string, on: boolean) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const togglePage = (on: boolean) =>
    setSelected((cur) => {
      const next = new Set(cur);
      for (const id of onPage) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  const jobText = job ? t(job.kind === "upload" ? "docs.uploading" : job.kind === "remove" ? "docs.removing" : "docs.retrying", { done: job.done, total: job.total }) : "";

  return (
    <Section title={t("docs.title")} help={t("docs.help", { max: LIMITS.documentsMax, mb: MAX_FILE_MB })}>
      <div className="row">
        <label className={`btn secondary file${busy ? " disabled" : ""}`}>
          {t("docs.upload")}
          <input
            type="file"
            multiple
            disabled={busy}
            aria-label={t("docs.upload.label")}
            accept=".txt,.md,.markdown,.csv,.tsv,.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp"
            onChange={async (e) => {
              const files = e.target.files;
              await upload(files);
              e.target.value = "";
            }}
          />
        </label>
        {busy && (
          <button type="button" className="btn ghost" onClick={() => (stop.current = true)}>
            {t("docs.stop")}
          </button>
        )}
      </div>
      <div role="status" aria-live="polite">
        {job && (
          <p className="hint">
            {jobText} <progress value={job.done} max={job.total} aria-label={jobText} />
          </p>
        )}
        {!job && counts.total > 0 && (
          <p className="hint">
            {t("docs.summary", { total: counts.total, ready: counts.ready, working: counts.working, attention: counts.attention })}
            {counts.working > 0 && <> <progress value={counts.total - counts.working} max={counts.total} aria-label={t("docs.progress")} /></>}
          </p>
        )}
      </div>

      {counts.total > 0 && (
        <>
          <div className="grid2">
            <Field label={t("docs.search")} htmlFor="docs-search">
              <input id="docs-search" type="search" value={q} autoComplete="off" onChange={(e) => (setQ(e.target.value), setPage(1))} />
            </Field>
            <Field label={t("docs.filter")} htmlFor="docs-filter">
              <select id="docs-filter" value={filter} onChange={(e) => (setFilter(e.target.value as DocFilter), setPage(1))}>
                {(["all", "ready", "working", "attention", "unsupported"] as const).map((f) => (
                  <option key={f} value={f}>
                    {t(`docs.f.${f}` as never)}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          {counts.attention > 0 && (
            <button type="button" className="btn secondary" disabled={busy} onClick={() => void retryMany(docs.filter((d) => d.status === "error").map((d) => d.id))}>
              {t("docs.retryAll", { n: counts.attention })}
            </button>
          )}

          {matching.length === 0 ? (
            <p className="hint">{t("docs.none")}</p>
          ) : (
            <>
              <div className="row">
                <label className="inline">
                  <input type="checkbox" checked={allOnPage} disabled={busy} onChange={(e) => togglePage(e.target.checked)} /> {t("docs.selectPage")}
                </label>
                {allOnPage && matching.length > onPage.length && selected.size < matching.length && (
                  <button type="button" className="btn ghost" onClick={() => setSelected(new Set(matching.map((d) => d.id)))}>
                    {t("docs.selectAll", { n: matching.length })}
                  </button>
                )}
              </div>
              {selected.size > 0 && (
                <div className="row" role="group" aria-label={t("docs.bulk")}>
                  <strong>{t("docs.selected", { n: selected.size })}</strong>
                  {!confirming ? (
                    <>
                      <button type="button" className="btn secondary" disabled={busy} onClick={() => setConfirming(true)}>
                        {t("docs.removeSel")}
                      </button>
                      <button type="button" className="btn ghost" onClick={() => setSelected(new Set())}>
                        {t("docs.clear")}
                      </button>
                    </>
                  ) : (
                    <span className="row" role="alert">
                      <span>{t("docs.confirm", { n: selected.size })}</span>
                      <button type="button" className="btn secondary" onClick={() => void removeMany([...selected])}>
                        {t("docs.confirmYes")}
                      </button>
                      <button type="button" className="btn ghost" onClick={() => setConfirming(false)}>
                        {t("docs.keep")}
                      </button>
                    </span>
                  )}
                </div>
              )}
              <ul className="sources" aria-label={t("docs.title")}>
                {view.items.map((s) => (
                  <li key={s.id} data-status={s.status}>
                    <div className="row between">
                      <label className="inline">
                        <input type="checkbox" checked={selected.has(s.id)} disabled={busy} onChange={(e) => toggle(s.id, e.target.checked)} aria-label={t("docs.select", { name: s.name })} />
                        <span className="source-name">{s.name}</span>
                      </label>
                      <span className="row">
                        <Badge tone={TONE[s.status]}>{t(`docs.s.${s.status}` as never)}</Badge>
                        {s.status === "indexed" && <span className="hint">{t("docs.passages", { n: s.chunks })}</span>}
                        <OcrControl api={api} agentId={agentId} source={s} refresh={refresh} />
                        {s.status === "error" && (
                          <button type="button" className="btn ghost" disabled={busy} onClick={() => void retryMany([s.id])} aria-label={t("docs.retry", { name: s.name })}>
                            {t("docs.retryOne")}
                          </button>
                        )}
                        <button type="button" className="btn ghost" disabled={busy} onClick={() => void removeMany([s.id])} aria-label={t("docs.remove", { name: s.name })}>
                          {t("docs.removeOne")}
                        </button>
                      </span>
                    </div>
                    {s.error && <p className={s.status === "error" ? "error" : "hint"}>{s.error}</p>}
                  </li>
                ))}
              </ul>
              <nav className="row between" aria-label={t("docs.pages")}>
                <span className="hint" aria-live="polite">
                  {t("docs.showing", { from: view.from, to: view.to, n: matching.length })}
                </span>
                {view.pages > 1 && (
                  <span className="row">
                    <button type="button" className="btn ghost" disabled={view.page <= 1} onClick={() => setPage(view.page - 1)}>
                      {t("docs.prev")}
                    </button>
                    <span>{t("docs.page", { p: view.page, pages: view.pages })}</span>
                    <button type="button" className="btn ghost" disabled={view.page >= view.pages} onClick={() => setPage(view.page + 1)}>
                      {t("docs.next")}
                    </button>
                  </span>
                )}
              </nav>
            </>
          )}
        </>
      )}
    </Section>
  );
}

export { PAGE_SIZE };
